<script setup lang="ts">
import { onBeforeMount, provide, ref, watch } from 'vue'

import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'

import GoogleMap from '@/components/GoogleMap'
import LeftMenu from '@/components/LeftMenu.vue'

import config from '@/config'
import language from '@/lang/index.json'
import useCurrentUserLocation from '@/lib/useCurrentUserLocation'
import useCenter from '@/lib/useMapCenter'
import { useMobile } from '@/lib/useMedia'
import type { Event, Events, Language, Place, Store } from '@/types'

const type = 'event'
provide('type', type)

const router = useRoute()
const { locale } = useI18n()
const isMobile = useMobile()
const lang = (router.params.lang as Language) || 'de'
locale.value = lang

const map = ref<google.maps.Map>()
const { center, updateCenter } = useCenter()

const events = ref<Events>()
const loaded = ref<boolean>(false)
const eventsDataUrl = ref<string>(import.meta.env.PUBLIC_GEO_JSON_EVENTS)
const showEventsList = ref<boolean>(false)
const selectedEvent = ref<(Store & Event) | null>(null)

const settings = config.settings[lang][isMobile.value ? 'mobile' : 'desktop']

const shouldShow = (show: boolean) => {
  showEventsList.value = show
}

const navigateTo = (place: Place): void => {
  showEventsList.value = true
  selectedEvent.value = place as (Store & Event) | null
  updateCenter(place.geometry?.coordinates)
  map.value?.panTo(place.geometry?.coordinates)
  if (map.value?.getZoom() !== config.settings.panToZoom) {
    map.value?.setZoom(config.settings.panToZoom)
  }
}

onBeforeMount(async () => {
  try {
    if (router.params.subtype) {
      eventsDataUrl.value = import.meta.env[
        `PUBLIC_GEO_JSON_EVENTS_${String(router.params.subtype).toUpperCase()}`
      ]
    }

    const response = await (await fetch(eventsDataUrl.value)).json()

    events.value = response

    map.value = await GoogleMap({
      response,
      lang,
      navigateTo,
    })

    loaded.value = true
  } catch (error) {
    console.log(error)
    map.value = await GoogleMap({
      response: { features: [] },
      lang,
      navigateTo,
    })
  }
})

watch(map, () => {
  useCurrentUserLocation(map.value, language[lang].locationDisabled, lang)
})
</script>
<template>
  <div id="map" />
  <LeftMenu
    v-if="loaded && events"
    :map-ref="map"
    :places="events"
    :selected-place="selectedEvent"
    :center="center"
    :default-zoom="settings.defaultZoom"
    :show-store-list="showEventsList"
    @navigate-to="navigateTo"
    @remove-store="selectedEvent = null"
    @save-center="updateCenter"
    @should-show="shouldShow"
  />
</template>
<style>
#map {
  height: 100%;
}

#app {
  height: 100%;
}

html,
body {
  margin: 0;
  padding: 0;
  height: 100%;
}
</style>
